import { combineReducers } from 'redux';

import _ from 'lodash';
import { assoc } from 'lodash/fp';
import * as actions from '../actions/action-types';
import { moveItem } from '../../helpers';

const treeIntoFlags = (tree) => {
  return Object.keys(tree).reduce((badge, key) => {
    badge[key] = true; // Set Each Badge in the tree as to true ex: { 530138-Grid(pin):true }
    return badge;
  }, {});
};

// Remove a child component from its parent's children array
const removeChild = (componentId, parentId, tree) => {
  if (tree[parentId]) {
    tree[parentId].children = tree[parentId].children.filter(
      (childId) => childId !== componentId
    );
  }
  return tree;
};

// Delete a component from the tree
const deleteComponent = (componentId, tree) => {
  const parentId = tree[componentId]?.parentId;
  const updatedTree = { ...tree };

  // Remove the component itself
  delete updatedTree[componentId];

  // Remove the component from its parent's children
  if (parentId) {
    removeChild(componentId, parentId, updatedTree);
  }

  return updatedTree;
};

const moveComponent = ({ movingId, siblingId }, state) => {
  const oldParentId = state[movingId]?.parentId;
  const newParentId = state[siblingId]?.parentId;

  // Create a shallow copy of the state
  const updatedState = { ...state };

  // Update the parentId of the moving component
  updatedState[movingId] = { ...updatedState[movingId], parentId: newParentId };

  // Remove the moving component from its old parent's children
  if (oldParentId && updatedState[oldParentId]?.children) {
    updatedState[oldParentId].children = updatedState[
      oldParentId
    ].children.filter((child) => child !== movingId);
  }

  // Add the moving component to its new parent's children
  const newParentChildren = updatedState[newParentId]?.children || [];
  const siblingIndex = newParentChildren.findIndex((id) => siblingId === id);
  const updatedChildren = moveItem(
    [...newParentChildren, movingId], // Concatenate the moving component
    newParentChildren.length, // Original length before adding the new child
    siblingIndex // Find the index of the siblingId to insert the moving component
  );

  updatedState[newParentId].children = updatedChildren;

  return updatedState;
};

export const flatOutline = (state = {}, action) => {
  const { component, order = Infinity, type, id } = action;

  switch (type) {
    case actions.OUTLINE_RECEIVED:
      return action.outline;
    case actions.COMPONENT_UPDATED:
      return {
        ...state,
        [component.id]: component,
      };
    case actions.COMPONENT_DELETED:
      return deleteComponent(id, state);
    case actions.COMPONENT_ADDED:
      return {
        ...state,

        // add component
        [component.id]: {
          ...component,
          props: { ...component.props, untouched: true },
        },

        // inform parent
        [component.parentId]: {
          ...state[component.parentId],

          // splice in new component's id
          children: [
            ...(state[component?.parentId]?.children || []).slice(0, order),
            component.id,
            ...(state[component?.parentId]?.children || []).slice(order),
          ],
        },
      };
    case actions.ADD_REPEATABLE:
      return assoc(
        action.path,
        [
          ..._.get(state, action.path, []),
          { $unique_key: new Date().getTime(), ...action.repeatable },
        ],
        state
      );
    case actions.MOVE_REPEATABLE:
      return assoc(
        action.path,
        moveItem(
          _.get(state, action.path, []),
          action.fromIndex,
          action.toIndex
        ),
        state
      );
    case actions.DELETE_REPEATABLE:
      return assoc(
        action.path,
        _.get(state, action.path).filter((x, i) => i !== action.index),
        state
      );
    case actions.DUPLICATE_REPEATABLE: {
      const [parentKey, propsKey, itemsKey] = action.path;
      const { index } = action;

      const parent = state[parentKey];
      const props = parent[propsKey];
      const items = props[itemsKey];
      const item = items[index];

      return {
        ...state,
        [parentKey]: {
          ...parent,
          [propsKey]: {
            ...props,
            [itemsKey]: [
              ...items,
              {
                ..._.cloneDeep(item),
                $unique_key: new Date().getTime(),
              },
            ],
          },
        },
      };
    }
    case actions.DUPLICATE_GRID_ITEM_COMPONENT: {
      const [parentKey, propsKey, gridItemsKey, gridItemIndexKey] = action.path;
      const { index } = action;

      const parent = state[parentKey];
      const props = parent[propsKey];
      const gridItems = props[gridItemsKey];

      const selectedItemComponent =
        gridItems?.[gridItemIndexKey]?.itemComponents?.[index];

      const itemComponents = [
        ...gridItems?.[gridItemIndexKey]?.itemComponents,
        {
          ..._.cloneDeep(selectedItemComponent),
          $unique_key: new Date().getTime(),
        },
      ];

      if (gridItems?.[gridItemIndexKey]?.itemComponents?.length) {
        const updatedGridItemsWithItemComponents = [...gridItems];
        updatedGridItemsWithItemComponents[gridItemIndexKey] = {
          ...gridItems[gridItemIndexKey],
          itemComponents,
        };

        return {
          ...state,
          [parentKey]: {
            ...parent,
            [propsKey]: {
              ...props,
              [gridItemsKey]: [...updatedGridItemsWithItemComponents],
            },
          },
        };
      }

      return {
        ...state,
      };
    }
    default:
      return state;
    case actions.MOVE_COMPONENT:
      return moveComponent(action, state);
  }
};

const builderLoaded = (state = false, action) => {
  switch (action.type) {
    case actions.BUILDER_LOADED:
      return true;
    default:
      return state;
  }
};

const selected = (state = '', action) => {
  switch (action.type) {
    case actions.COMPONENT_SELECTED:
      return action.id;
    case actions.COMPONENT_ADDED:
      return action.component.id;
    case actions.COMPONENT_DELETED:
      return action.id === state ? '' : state;
    case actions.EDITOR_CLOSE:
      return '';
    default:
      return state;
  }
};

const builderVersion = (state = '', action) => {
  switch (action.type) {
    case actions.SET_BUILDER_VERSION:
      return action.version;
    default:
      return state;
  }
};

const initialEditor = {
  open: false,
  tab: 0,
  width: 350,
  height: 460,
  dock: 'left',
  showAllMediaViewPorts: false,
  mobileExperienceView: '',
};

const editor = (state = initialEditor, action) => {
  const { tab } = action;
  switch (action.type) {
    case actions.EDITOR_OPEN:
      return {
        ...state,
        open: true,
      };
    case actions.EDITOR_CLOSE:
    case actions.COMPONENT_DELETED:
    case actions.OUTLINE_RECEIVED: // close editor when loaded JSON
      return {
        ...state,
        open: false,
      };
    case actions.EDITOR_TAB_SELECT:
      return {
        ...state,
        tab,
      };
    case actions.COMPONENT_SELECTED:
    case actions.COMPONENT_ADDED:
      return {
        ...state,
        tab: 1,
      };
    case actions.EDITOR_UPDATE_HEIGHT:
      return action.height >= 20
        ? {
            ...state,
            height: action.height,
          }
        : state;
    case actions.EDITOR_UPDATE_WIDTH:
      return action.width >= 350
        ? {
            ...state,
            width: action.width,
          }
        : state;
    case actions.EDITOR_UPDATE_DOCK:
      return action.dock === state.dock
        ? {
            ...state,
            open: false,
          }
        : {
            ...state,
            dock: action.dock,
          };

    case actions.EDITOR_SHOW_ALL_VIEW_PORTS:
      return {
        ...state,
        showAllMediaViewPorts: action.payload,
      };
    case actions.EDITOR_TOGGLE_ALL_VIEW_PORTS:
      return {
        ...state,
        showAllMediaViewPorts: !state.showAllMediaViewPorts,
      };
    case actions.EDITOR_CHANGE_MOBILE_EXPERIENCE_VIEW:
      return {
        ...state,
        mobileExperienceView: action.payload,
      };
    default:
      return state;
  }
};

const badges = (state = {}, action) => {
  switch (action.type) {
    case actions.OUTLINE_RECEIVED:
      return treeIntoFlags(action.outline);
    case actions.COMPONENT_ADDED:
      return {
        ...state,
        [action.component.id]: true,
      };
    case actions.TOGGLE_BADGE:
      return {
        ...state,
        [action.componentId]: !state[action.componentId],
      };
    case actions.CLOSE_ALL_BADGES:
      return {};
    default:
      return state;
  }
};

const watchQueryParams = (state = false, action) => {
  switch (action.type) {
    case actions.SHOULD_WATCH_QUERY_PARAMS:
      return action.watchQueryParams;
    default:
      return state;
  }
};

const alwaysRenderInBuilder = (state = [], action) => {
  switch (action.type) {
    case actions.ALWAYS_RENDER_IN_BUILDER:
      return state.concat([action.id]);
    default:
      return state;
  }
};

const sorting = (state = {}, action) => {
  switch (action.type) {
    case actions.START_SORTING:
      return {
        ...state,
        componentId: action.componentId,
      };
    case actions.STOP_SORTING:
      return {
        ...state,
        componentId: undefined,
      };
    case actions.MOVE_COMPONENT:
      return {
        ...state,
        componentId: undefined,
      };
    default:
      return state;
  }
};

export default combineReducers({
  flatOutline,
  builderLoaded,
  selected,
  editor,
  badges,
  watchQueryParams,
  alwaysRenderInBuilder,
  sorting,
  version: builderVersion,
});
